import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, ShieldCheck, Lock } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { setAdminTokens } from "@/lib/adminSession";
import BrandLogo from "@/components/common/BrandLogo";

/**
 * Hidden, hardened admin login page.
 * Mounted at /admin/login — never linked from the public site.
 * Submits to /api/admin-auth/login (separate cookie, separate collection).
 */
export default function AdminLogin() {
  const nav = useNavigate();
  const { admin, setAdmin } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If already signed in, jump straight to dashboard
  useEffect(() => {
    if (admin && admin.id) nav("/admin/dashboard", { replace: true });
  }, [admin, nav]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/admin-auth/login", { email, password });
      if (data.admin_access_token) {
        setAdminTokens(data.admin_access_token, data.admin_refresh_token);
      }
      setAdmin(data.admin);
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(240,184,58,0.12) 0%, transparent 55%), #0A0F1F",
      }}
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-[440px]">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3 mb-8">
          <BrandLogo size={64} />
          <div className="text-center">
            <div className="text-[15px] font-bold text-[#E5E9F2] leading-none">CoinMBX</div>
            <div className="text-[11px] text-[#F0B83A] tracking-[0.2em] uppercase mt-1">
              Operator Portal
            </div>
          </div>
        </div>

        <div
          className="rounded-[16px] border bg-[#0F1626]/90 backdrop-blur p-8"
          style={{ borderColor: "#1E2845", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)" }}
        >
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} className="text-[#F0B83A]" />
            <h1 className="text-[20px] font-bold text-[#E5E9F2] tracking-tight">
              Admin sign in
            </h1>
          </div>
          <p className="text-[12px] text-[#8892A8] mb-7">
            Restricted access · This portal is monitored. All sign-in attempts are logged.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label className="text-[12px] font-semibold text-[#8892A8] uppercase tracking-wider">
                Operator email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="mt-2 w-full px-4 py-3.5 rounded-[10px] border bg-[#0A0F1F] outline-none text-[#E5E9F2] text-[14px] focus:border-[#F0B83A] transition"
                style={{ borderColor: "#1E2845" }}
                placeholder="admin@coinmbx.com"
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[#8892A8] uppercase tracking-wider">
                Password
              </label>
              <div className="relative mt-2">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  className="w-full px-4 py-3.5 pr-11 rounded-[10px] border bg-[#0A0F1F] outline-none text-[#E5E9F2] text-[14px] focus:border-[#F0B83A] transition"
                  style={{ borderColor: "#1E2845" }}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8892A8] p-1"
                  tabIndex={-1}
                  aria-label="Toggle password"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-[13px] text-[#FCA5A5] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-[10px] px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full py-3.5 rounded-[10px] font-bold text-[14px] disabled:opacity-60 flex items-center justify-center gap-2 transition hover:opacity-90"
              style={{ background: "#F0B83A", color: "#0A0F1F" }}
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              <Lock size={14} /> Sign in to operator console
            </button>
          </form>

          <div className="mt-6 pt-5 border-t flex items-center justify-between text-[11px] text-[#5A6582]" style={{ borderColor: "#1E2845" }}>
            <span>Not an operator?</span>
            <a href="/" className="text-[#8892A8] hover:text-[#F0B83A] transition">
              Back to coinmbx.com →
            </a>
          </div>
        </div>

        <p className="text-center mt-6 text-[10px] text-[#5A6582] tracking-wider uppercase">
          CoinMBX Internal · Authorized personnel only
        </p>
      </div>
    </div>
  );
}
